/**
 * Opens the Asset Library in an Office Dialog (a modal window floating over
 * PowerPoint) and keeps a live two-way channel to it for as long as it's up.
 *
 * Unlike create-slide's dialog — which collects a config once and closes — this
 * window stays open and converses with the pane, because everything it needs is
 * something only the pane can do: an Office Dialog can neither authorise the
 * slide-template fetch (no AuthStateProvider, so no bearer token and no
 * refresh) nor call PowerPoint.run(). So the window asks, the pane answers, and
 * the window keeps its own loading/error/retry UI throughout. See
 * services/assetLibraryDialogChannel.ts for the message contract.
 */

import { logEvent, logOfficeError } from "../../../../../telemetry";
import {
  parseDialogToPaneMessage,
  serialiseMessage,
  type DialogToPaneMessage,
  type PaneToDialogMessage,
} from "../services/assetLibraryDialogChannel";

export { canUseDialog } from "../../create-slide/hooks/openCreateSlidesDialog";

const DECKORATE_CONNECTOR = "deckorate";

/**
 * Percentage-of-screen size for the Office Dialog window itself. The panel
 * fills this window edge-to-edge (AssetLibraryDialogApp.module.css's
 * .panelFill strips .panel's own margin/caps/border/shadow for this context),
 * so this size IS the visible card's size — no separate box floating inside.
 */
const DIALOG_SIZE = { height: 85, width: 85 } as const;

/** Relative path of the dialog page served from the add-in origin. */
const DIALOG_PAGE = "/asset-library-dialog.html";

const OPEN_FAILED_MESSAGE = "Couldn't open the Asset Library window. Please try again.";

/** A live handle on the open dialog window. */
export interface AssetLibrarySession {
  /**
   * Pushes a message into the window. No-ops (with a telemetry trace) when the
   * host predates DialogApi 1.2 and has no messageChild, or when the window is
   * already gone — a send is always best-effort, never a reason to throw.
   */
  readonly send: (message: PaneToDialogMessage) => void;
  /** Tears the window down. Safe to call more than once. */
  readonly close: () => void;
}

export interface AssetLibraryDialogHandlers {
  /** A message arrived from the window. Unrecognised payloads never reach here. */
  readonly onMessage: (message: DialogToPaneMessage, session: AssetLibrarySession) => void;
  /** The window is gone (user closed it, host tore it down, or we closed it). */
  readonly onClosed: () => void;
}

/**
 * Whether the host supports pane → dialog messages (DialogApi 1.2+). Without
 * it the window can talk to us but we can't answer, so the picker would sit on
 * its spinner forever — the caller checks this and degrades instead.
 */
export function canMessageDialog(dialog: Office.Dialog): boolean {
  return typeof (dialog as { messageChild?: unknown }).messageChild === "function";
}

/**
 * Opens the window. Resolves with a session once it's up; rejects with a
 * user-facing message if Office refuses to open it (the technical detail goes
 * to telemetry, never the console).
 */
export function openAssetLibraryDialog(handlers: AssetLibraryDialogHandlers): Promise<AssetLibrarySession> {
  return new Promise((resolve, reject) => {
    if (typeof Office === "undefined" || typeof Office.context?.ui?.displayDialogAsync !== "function") {
      logEvent("connector.action.invoke", "warn", {
        connectorSystemName: DECKORATE_CONNECTOR,
        reason: "asset-library-dialog-api-unavailable",
      });
      reject(new Error(OPEN_FAILED_MESSAGE));
      return;
    }

    const url = `${globalThis.location.origin}${DIALOG_PAGE}`;
    Office.context.ui.displayDialogAsync(url, { ...DIALOG_SIZE }, (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        const err = asyncResult.error;
        logOfficeError(
          new Error(`Office failed to open the Asset Library dialog (code ${err?.code ?? "?"}): ${err?.message ?? "unknown error"}`),
          "asset-library-dialog-open",
        );
        reject(new Error(OPEN_FAILED_MESSAGE));
        return;
      }

      logEvent("connector.action.invoke", "info", {
        connectorSystemName: DECKORATE_CONNECTOR,
        reason: "asset-library-dialog-opened",
      });

      const dialog = asyncResult.value;
      let closed = false;

      const session: AssetLibrarySession = {
        send: (message) => {
          if (closed) return;
          if (!canMessageDialog(dialog)) {
            logEvent("connector.action.invoke", "warn", {
              connectorSystemName: DECKORATE_CONNECTOR,
              reason: "asset-library-message-child-unavailable",
            });
            return;
          }
          try {
            dialog.messageChild(serialiseMessage(message));
          } catch {
            // The window went away between our check and the send (user closed
            // it mid-flight). Nothing to recover — onClosed handles cleanup.
            logEvent("connector.action.invoke", "warn", {
              connectorSystemName: DECKORATE_CONNECTOR,
              reason: "asset-library-message-child-failed",
            });
          }
        },
        close: () => {
          if (closed) return;
          closed = true;
          try {
            dialog.close();
          } catch {
            // Expected when the host already tore the window down (× / host
            // close raced this call) — worth a trace so a real regression here
            // isn't invisible, but not an error.
            logEvent("connector.action.invoke", "warn", {
              connectorSystemName: DECKORATE_CONNECTOR,
              reason: "asset-library-dialog-already-closed",
            });
          }
          handlers.onClosed();
        },
      };

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
        const message = parseDialogToPaneMessage(arg);
        if (!message) {
          logEvent("connector.action.invoke", "warn", {
            connectorSystemName: DECKORATE_CONNECTOR,
            reason: "asset-library-dialog-message-unrecognised",
          });
          return;
        }
        handlers.onMessage(message, session);
      });

      // The window went away on its own (× / host close). Mark it closed so no
      // later send tries to reach a dead window, then let the caller clean up.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        if (closed) return;
        closed = true;
        handlers.onClosed();
      });

      resolve(session);
    });
  });
}
